import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { BetaProvider } from "./context/BetaContext";
import { ApiBaseProvider } from "./context/ApiBaseContext";
import ProtectedRoute from "./components/ProtectedRoute";
import BetaGate from "./components/BetaGate";
import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MyProjects from "./pages/MyProjects";
import CreateProject from "./pages/CreateProject";
import ServiceProductionAccessPage from "./pages/ServiceProductionAccessPage";
import JoinProject from "./pages/JoinProject";
import ProjectHome from "./pages/ProjectHome";
import PrepMode from "./pages/PrepMode";
import OnSetMode from "./pages/OnSetMode";
import ScenesReview from "./pages/ScenesReview";
import ScenesHub from "./pages/ScenesHub";
import CastHub from "./pages/CastHub";
import GalleryPage from "./pages/GalleryPage";
import SceneDetail from "./pages/SceneDetail";
import ImageDetail from "./pages/ImageDetail";
import UnassignedPage from "./pages/UnassignedPage";
import UnassignedArchivePage from "./pages/UnassignedArchivePage";
import FlagsPage from "./pages/FlagsPage";
import ProjectSettings from "./pages/ProjectSettings";
import ScriptSetupPage from "./pages/ScriptSetupPage";
import ScriptPage from "./pages/ScriptPage";
import JoinRedirect from "./pages/JoinRedirect";
import JoinBetaProject from "./pages/JoinBetaProject";
import CharacterDetail from "./pages/CharacterDetail";
import BulkCastEdit from "./pages/BulkCastEdit";
import BulkSceneEdit from "./pages/BulkSceneEdit";
import SupervisorNotesPage from "./pages/SupervisorNotesPage";
import SchedulePage from "./pages/SchedulePage";
import BackupsPage from "./pages/BackupsPage";
import AtticPage from "./pages/AtticPage";
import DocumentViewerPage from "./pages/DocumentViewerPage";
import SchedulePreviewPage from "./pages/SchedulePreviewPage";
import KitPage from "./pages/KitPage";
import KitDepartmentPage from "./pages/kit/KitDepartmentPage";
import KitCategoryPage from "./pages/kit/KitCategoryPage";
import PdfViewerPage from "./pages/PdfViewerPage";
import BetaHome from "./pages/beta/BetaHome";
import BetaProduction from "./pages/beta/BetaProduction";
import BetaProject from "./pages/beta/BetaProject";
import BetaDepartment from "./pages/beta/BetaDepartment";
import BetaHMOverview from "./pages/beta/hm/BetaHMOverview";
import { BetaHMCharacters, BetaHMCharacterDetail } from "./pages/beta/hm/BetaHMCharacters";
import { BetaHMScenes, BetaHMSceneDetail } from "./pages/beta/hm/BetaHMScenes";
import BetaHmWorkspace from "./components/BetaHmWorkspace";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { notifyRoleChange } from "./hooks/useProjectRoleRefresh";
import { startFlushWorker } from "./lib/offline";
import { api } from "./lib/api";

function LoginGate() {
    const { status } = useAuth();
    // Iter-158.12.7 UAT fix — honour the `?next=` deep-link param at
    // the gate level. Without this, when auth state flips to
    // "authed" after login, LoginGate fires a `<Navigate to="/" />`
    // that WINS the race against AuthPage.goNext(), silently
    // dropping the beta invite deep link. Only accept in-app
    // absolute paths (guard against open-redirect abuse).
    const [searchParams] = useSearchParams();
    if (status === "authed") {
        const rawNext = searchParams.get("next") || "";
        const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//")
            ? rawNext
            : "/";
        return <Navigate to={nextPath} replace />;
    }
    return <AuthPage />;
}

function App() {
    // Iter-63 PART 10: global scroll-jitter guard. While the user is
    // mid-scroll we toggle `data-scrolling="1"` on <html>; the
    // matching CSS selectors in index.css suppress :active / :hover
    // tones on `.tap` so list rows stop darkening/scaling under a
    // flick. We also briefly mute pointer events on the `.tap` row
    // that received the touchstart if the touch turns into a move
    // beyond a 6px threshold.
    useEffect(() => {
        const root = document.documentElement;
        let scrollIdleTimer = null;
        let touchStartY = 0;
        const onScroll = () => {
            root.setAttribute("data-scrolling", "1");
            if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
            scrollIdleTimer = setTimeout(() => {
                root.setAttribute("data-scrolling", "0");
            }, 90);
        };
        const onTouchStart = (e) => {
            touchStartY = e.touches?.[0]?.clientY ?? 0;
        };
        const onTouchMove = (e) => {
            const y = e.touches?.[0]?.clientY ?? touchStartY;
            if (Math.abs(y - touchStartY) > 6) {
                root.setAttribute("data-scrolling", "1");
                if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
                scrollIdleTimer = setTimeout(() => {
                    root.setAttribute("data-scrolling", "0");
                }, 120);
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true, capture: true });
        window.addEventListener("touchstart", onTouchStart, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
        };
    }, []);

    // Iter-157I P0 — Upload queue auto-flush. Once at boot + on
    // every online / visibility-restored transition, walk the
    // pending-uploads queue and retry each item. Success → drop
    // from queue. Network failure → leave in place for next tick.
    // Permanent failure → stamp last_error so the UI can show
    // "Failed" instead of "Queued". Idempotent.
    useEffect(() => {
        const stop = startFlushWorker(api);
        return stop;
    }, []);

    // Iter-157E P0(5)+(6) — Global role-change toaster. The axios
    // success interceptor in lib/api.js dispatches
    // `osf:role-changed` whenever a GET /projects/{id} response
    // returns a role different from the last-cached one for that
    // project. We listen here so the toast lands no matter which
    // page the user happens to be on (ProjectHome, ScenesHub,
    // SceneDetail, OnSetMode, etc.). The per-page detection on
    // ProjectHome / ProjectSettings remains in place as a belt-
    // and-braces — sonner's de-dupe means the user sees the
    // message exactly once.
    useEffect(() => {
        const onRoleChange = (ev) => {
            try {
                const d = ev?.detail || {};
                notifyRoleChange(d.previous_role, d.next_role, d.project_name);
            } catch { /* never break over a toast */ }
        };
        window.addEventListener("osf:role-changed", onRoleChange);
        return () =>
            window.removeEventListener("osf:role-changed", onRoleChange);
    }, []);

    return (
        <ThemeProvider>
            <AppShell />
        </ThemeProvider>
    );
}

function AppShell() {
    const { theme } = useTheme();
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <BetaProvider>
                    <ApiBaseProvider value="/projects">
                    {/*
                      Iter-153 — toast position pinned BELOW the persistent
                      top header (project nav / back button / settings).
                      Using `top-center` with an `offset` of 72px keeps the
                      familiar top-anchored toasts visible while never
                      covering the header — escape / recovery paths stay
                      reachable at all times.
                      Iter-156A.4 — explicit duration tuning per pilot
                      validation feedback that toasts lingered too long.
                      Sonner default is ~4s; pilot users reported it felt
                      like 6-8s subjectively (toasts stack on top of each
                      other on iPad). Drop to 3s for info/success, keep
                      errors longer at 5s, allow swipe/click dismiss, and
                      cap the visible stack at 3.
                    */}
                    <Toaster
                        theme={theme}
                        position="top-center"
                        offset="72px"
                        richColors
                        duration={3000}
                        visibleToasts={3}
                        closeButton
                        toastOptions={{
                            duration: 3000,
                            classNames: {
                                error: "duration-[5000ms]",
                            },
                        }}
                    />
                    <RouteErrorBoundary>
                        <Routes>
                        <Route path="/login" element={<LoginGate />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/join/:code" element={<JoinRedirect />} />
                        <Route path="/join/beta/:code" element={<JoinBetaProject />} />
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <MyProjects />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/new"
                            element={
                                <ProtectedRoute>
                                    <CreateProject />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-158.13.2.20f.4 — service-operator surface.
                            Server-side gate is authoritative; the
                            component itself probes /service/status and
                            renders a "not authorised" fallback for any
                            non-operator that reaches the URL directly. */}
                        <Route
                            path="/service/production-access"
                            element={
                                <ProtectedRoute>
                                    <ServiceProductionAccessPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/join"
                            element={
                                <ProtectedRoute>
                                    <JoinProject />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId"
                            element={
                                <ProtectedRoute>
                                    <ProjectHome />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/prep"
                            element={
                                <ProtectedRoute>
                                    <PrepMode />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/onset"
                            element={
                                <ProtectedRoute>
                                    <OnSetMode />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/scenes/review"
                            element={
                                <ProtectedRoute>
                                    <ScenesReview />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/scenes/:sceneId"
                            element={
                                <ProtectedRoute>
                                    <SceneDetail />
                                </ProtectedRoute>
                            }
                        />
                        {/* Neutral project-level Scenes hub. Quick Access > Scenes lands here. */}
                        <Route
                            path="/projects/:projectId/scenes"
                            element={
                                <ProtectedRoute>
                                    <ScenesHub />
                                </ProtectedRoute>
                            }
                        />
                        {/* Neutral project-level Cast hub. Quick Access > Cast lands here. */}
                        <Route
                            path="/projects/:projectId/cast"
                            element={
                                <ProtectedRoute>
                                    <CastHub />
                                </ProtectedRoute>
                            }
                        />
                        {/* iter-55: Bulk Cast Edit — owner-only. Inline
                            edit numbers + actor + notes for many cast
                            members at once after a script import. */}
                        <Route
                            path="/projects/:projectId/cast/bulk"
                            element={
                                <ProtectedRoute>
                                    <BulkCastEdit />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-158.13.2.20f.42-final — Bulk Edit Scenes.
                            Per-field auto-save on blur (reuses the
                            BulkCastEdit EditableField). Expand-on-demand
                            drill-down over the existing
                            /scenes/{sid}/continuity + hm-breakdown-entries
                            endpoints for Scene → Character → AI findings.
                            Character continuity + AI findings inside the
                            drill-down are READ-ONLY here — edits happen
                            on the canonical Character workspace. */}
                        <Route
                            path="/projects/:projectId/scenes/bulk"
                            element={
                                <ProtectedRoute>
                                    <BulkSceneEdit />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-154 — Gallery mode. Visual continuity
                            browsing by character or by scene. Read-only
                            hub over existing state_entries; no new data
                            model. */}
                        <Route
                            path="/projects/:projectId/gallery"
                            element={
                                <ProtectedRoute>
                                    <GalleryPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Per-character continuity stream. Entered from
                            CastHub (tap on a character card) or from On
                            Set / Fittings search results. */}
                        <Route
                            path="/projects/:projectId/characters/:characterId"
                            element={
                                <ProtectedRoute>
                                    <CharacterDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/state-entries/:entryId"
                            element={
                                <ProtectedRoute>
                                    <ImageDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/unassigned"
                            element={
                                <ProtectedRoute>
                                    <UnassignedPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/unassigned/archive"
                            element={
                                <ProtectedRoute>
                                    <UnassignedArchivePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/flags"
                            element={
                                <ProtectedRoute>
                                    <FlagsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/settings"
                            element={
                                <ProtectedRoute>
                                    <ProjectSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/script-setup"
                            element={
                                <ProtectedRoute>
                                    <ScriptSetupPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/script"
                            element={
                                <ProtectedRoute>
                                    <ScriptPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/supervisor-notes"
                            element={
                                <ProtectedRoute>
                                    <SupervisorNotesPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-131b — OSF-chromed document viewer. */}
                        <Route
                            path="/projects/:projectId/documents/:docId"
                            element={
                                <ProtectedRoute>
                                    <DocumentViewerPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-158P.1 — In-app PDF viewer. Keeps ProjectHeader
                            + a clear Back control above Script and Schedule
                            PDFs on mobile instead of punting the producer
                            into a headerless new browser tab. */}
                        <Route
                            path="/projects/:projectId/pdf-viewer"
                            element={
                                <ProtectedRoute>
                                    <PdfViewerPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:projectId/schedule"
                            element={
                                <ProtectedRoute>
                                    <SchedulePage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-157G P1 — Schedule preview inside the OSF
                            viewer chrome (consistency with change docs). */}
                        <Route
                            path="/projects/:projectId/schedules/:scheduleId/view"
                            element={
                                <ProtectedRoute>
                                    <SchedulePreviewPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-111 — project metadata backups (CSV exports
                            of the audit log + current snapshot). */}
                        <Route
                            path="/projects/:projectId/backups"
                            element={
                                <ProtectedRoute>
                                    <BackupsPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-157A — The Attic. Trust surface for project
                            history + restore points + Restore to a Copy. */}
                        <Route
                            path="/projects/:projectId/attic"
                            element={
                                <ProtectedRoute>
                                    <AtticPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-117 PART 7 — embedded OnSetKit. Loads
                            onsetkit.shop full-screen with an OSF chrome
                            so users feel they never left the app. */}
                        <Route
                            path="/projects/:projectId/kit"
                            element={
                                <ProtectedRoute>
                                    <KitPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter-158.0 — Multi-Department Beta area (owner-only). BetaGate reads
                            GET /api/beta/me and silently redirects non-beta users to "/". No
                            trace of these routes leaks into the legacy nav; a beta user opts
                            in by navigating directly to /beta. */}
                        <Route path="/beta" element={
                            <ProtectedRoute><BetaGate><BetaHome /></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/productions/:productionId" element={
                            <ProtectedRoute><BetaGate><BetaProduction /></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.13.2.20f.30 — Production landing must
                            resolve `useRouteBase()` to "/beta/projects" so
                            the shared `<ProjectHeader>` Home button routes
                            to the current Production landing
                            (`/beta/projects/:projectId`) rather than the
                            legacy `/projects/:projectId` route (which
                            silently 404's for Route-2 beta projects and
                            surfaces as "Project not found" — real-iPhone
                            UAT symptom). Previously BetaProject mounted
                            without an ApiBaseProvider, so useRouteBase()
                            fell back to the default "/projects". Same
                            wrapper `Production Management` / `Backups` /
                            `Attic` already use above. Zero touch to
                            BetaProject's internal render. */}
                        <Route path="/beta/projects/:projectId" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><BetaProject /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.13.2.20e.2 — Production Team &
                            Access route. Reuses the shared
                            `ProjectSettings` page under
                            `ApiBaseProvider value="/beta/projects"`
                            so every downstream T&A endpoint hits the
                            Production factory mount at
                            `/api/beta/projects/{pid}/...`. */}
                        <Route path="/beta/projects/:projectId/settings" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><ProjectSettings /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.13.2.20e.4 — Production-scope
                            Storage + Restore Points routes. Same
                            shared BackupsPage / AtticPage components
                            reused; ApiBaseProvider makes every
                            downstream fetch land under
                            /api/beta/projects/{pid}/backup/*
                            + /restore-points/*. */}
                        <Route path="/beta/projects/:projectId/backups" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><BackupsPage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/attic" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><AtticPage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/departments/:deptKey" element={
                            <ProtectedRoute><BetaGate><BetaDepartment /></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.3 — Beta Hair & Makeup workspace (Phase 1: read-only).
                            Iter-158.5.2 note: these simplified pages remain as the hidden
                            legacy-fallback route while the new /beta/hm-app/projects/*
                            namespace (below) hosts the visible transplant. Kept for one
                            more iteration; scheduled for removal in Iter-158.5.3+. */}
                        <Route path="/beta/projects/:projectId/hm" element={
                            <ProtectedRoute><BetaGate><BetaHMOverview /></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/hm/characters" element={
                            <ProtectedRoute><BetaGate><BetaHMCharacters /></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/hm/characters/:characterId" element={
                            <ProtectedRoute><BetaGate><BetaHMCharacterDetail /></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/hm/scenes" element={
                            <ProtectedRoute><BetaGate><BetaHMScenes /></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/hm/scenes/:sceneId" element={
                            <ProtectedRoute><BetaGate><BetaHMSceneDetail /></BetaGate></ProtectedRoute>
                        } />

                        {/* Iter-158.5.2 — Beta H&M VISIBLE TRANSPLANT. New namespace
                            `/beta/hm-app/projects/:projectId/*` renders the SAME shared
                            React components as the legacy Hair & Makeup workspace, but
                            every `useApiBase()` consumer inside those components now
                            talks to `/api/beta/hm-app/projects/*` (backed by
                            beta_hm_full.py + hm_core against the beta_* collection set).
                            A persistent 'Beta · Hair & Makeup' pill sits in the top-right
                            of every page in this namespace. Read-only in Phase 1 —
                            write endpoints are not registered on beta_hm_full.py yet,
                            so any mutation attempt from these shared components will
                            surface a clean 404 rather than corrupt beta data.

                            React Router path strings inside the shared components still
                            reference `/projects/...` — that navigation layer is scheduled
                            for its dedicated pass in Iter-158.5.2.a. Users who click
                            deeper navigation may bounce back into legacy for now; the
                            primary read surface (project home / cast / scenes /
                            character detail / scene detail / gallery / unassigned) works
                            end-to-end against beta data. */}
                        <Route path="/beta/hm-app/projects/:projectId" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><ProjectHome /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/cast" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><CastHub /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/characters/:characterId" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><CharacterDetail /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/scenes" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><ScenesHub /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/scenes/:sceneId" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><SceneDetail /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/gallery" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><GalleryPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/unassigned" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><UnassignedPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/state-entries/:entryId" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><ImageDetail /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.5.2.a — Namespace Seal. Mount every
                            shared page that could be reached via
                            navigation (Link / navigate / backHref) from
                            within the beta H&M workspace so no click can
                            escape the `/beta/hm-app/projects/*` namespace.
                            Write endpoints for these pages remain
                            unregistered in beta_hm_full.py — any mutation
                            attempt surfaces a clean 404/405 rather than
                            corrupt beta data. Read-only in Phase 1. */}
                        <Route path="/beta/hm-app/projects/:projectId/onset" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><OnSetMode /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/prep" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><PrepMode /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/scenes/review" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><ScenesReview /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/cast/bulk" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><BulkCastEdit /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/scenes/bulk" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><BulkSceneEdit /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/unassigned/archive" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><UnassignedArchivePage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/flags" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><FlagsPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/settings" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><ProjectSettings /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.13.2.20e.1 — Canonical Script
                            authority moved to Production. The H&M
                            script-setup route is retired; the
                            Production route lives directly under
                            /beta/projects/:pid/script-setup and
                            provides `apiBase="/beta/projects"` so
                            the reused `ScriptSetupPage` /
                            `ScriptSection` / `EpisodicScriptSetup`
                            components hit the Production Script
                            factory mount. */}
                        <Route path="/beta/projects/:projectId/script-setup" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><ScriptSetupPage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/script" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><ScriptPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/supervisor-notes" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><SupervisorNotesPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/documents/:docId" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><DocumentViewerPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/pdf-viewer" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><PdfViewerPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/schedule" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><SchedulePage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/schedules/:scheduleId/view" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><SchedulePreviewPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/backups" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><BackupsPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/attic" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><AtticPage /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/kit" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><KitDepartmentPage department="hair_makeup" /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/hm-app/projects/:projectId/kit/:categorySlug" element={
                            <ProtectedRoute><BetaGate><BetaHmWorkspace><KitCategoryPage department="hair_makeup" /></BetaHmWorkspace></BetaGate></ProtectedRoute>
                        } />
                        {/* Iter-158.13.2.20e.10 — Production Documents + Canonical Schedule */}
                        <Route path="/beta/projects/:projectId/supervisor-notes" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><SupervisorNotesPage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/documents/:docId" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><DocumentViewerPage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/schedule" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><SchedulePage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="/beta/projects/:projectId/schedules/:scheduleId/view" element={
                            <ProtectedRoute><BetaGate><ApiBaseProvider value="/beta/projects"><SchedulePreviewPage /></ApiBaseProvider></BetaGate></ProtectedRoute>
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </RouteErrorBoundary>
                    </ApiBaseProvider>
                    </BetaProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
